import { DatePipe } from '@angular/common';
import { Component, OnDestroy, OnInit, computed, inject, signal } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { catchError, of, switchMap } from 'rxjs';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { TextareaModule } from 'primeng/textarea';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinAmountPipe } from '../../shared/design-system/ain-amount.pipe';
import { CurrencySelectorComponent } from '../../shared/design-system/currency-selector.component';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { OfferingSelectorComponent } from '../../shared/design-system/offering-selector.component';
import { PartySelectorComponent } from '../../shared/design-system/party-selector.component';
import { CustomerSelectorComponent } from '../../shared/design-system/customer-selector.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { AttachmentContentService } from '../../shared/design-system/attachment-content.service';
import {
  CatalogOffering,
  CatalogService,
  MarketplaceDispute,
  MarketplaceSellerApplicationDocument,
  MarketplaceSeller,
  MarketplaceSellerOfferSummary,
  MarketplaceSellerOrder,
  MarketplaceSettlementSummary
} from './catalog.service';

type SellerTab = 'overview' | 'documents' | 'offers' | 'orders' | 'settlements' | 'disputes';

@Component({
  standalone: true,
  selector: 'ain-marketplace-seller-detail',
  imports: [
    AinAmountPipe,
    AinPageComponent,
    AinStateComponent,
    InfoHintComponent,
    OfferingSelectorComponent,
    PartySelectorComponent,
    CustomerSelectorComponent,
    CurrencySelectorComponent,
    RouterLink,
    FormsModule,
    ButtonModule,
    DialogModule,
    InputNumberModule,
    InputTextModule,
    TextareaModule,
    TableModule,
    DatePipe
  ],
  template: `
    <ain-page [title]="pageTitle()" [description]="t('catalog.marketplace.sellers.detail.description')">
      <ain-info-hint
        page-title-addon
        [titleKey]="'catalog.marketplace.help.detail.title'"
        [descriptionKey]="'catalog.marketplace.help.detail.description'"
        [bulletKeys]="[
          'catalog.marketplace.help.detail.bullets.tabs',
          'catalog.marketplace.help.detail.bullets.kyc',
          'catalog.marketplace.help.detail.bullets.offers'
        ]" />
      @if (loading()) {
        <ain-state type="loading" [title]="t('catalog.loading')" />
      } @else if (!seller()) {
        <ain-state type="error" [title]="t('catalog.marketplace.sellers.detail.notFound')" />
      } @else {
        <div class="toolbar">
          <a class="back-link" [routerLink]="['/marketplace/sellers']" [queryParams]="listQueryParams()">{{ t('catalog.action.backToList') }}</a>
          @if (canSubmit()) {
            <button pButton type="button" severity="secondary" [label]="t('catalog.marketplace.sellers.submit')" [disabled]="acting()" (click)="submitForReview()"></button>
          }
          @if (canApprove()) {
            <button pButton type="button" [label]="t('catalog.marketplace.sellers.approve')" [disabled]="acting()" (click)="openApprove()"></button>
          }
          @if (canSuspend()) {
            <button pButton type="button" severity="danger" [label]="t('catalog.marketplace.sellers.suspend')" [disabled]="acting()" (click)="openStatusDialog('suspend')"></button>
          }
          @if (canReactivate()) {
            <button pButton type="button" [label]="t('catalog.marketplace.sellers.reactivate')" [disabled]="acting()" (click)="reactivate()"></button>
          }
          @if (canReject()) {
            <button pButton type="button" severity="danger" [label]="t('catalog.marketplace.sellers.reject')" [disabled]="acting()" (click)="openStatusDialog('reject')"></button>
          }
        </div>

        <nav class="tabs">
          @for (tab of tabs; track tab.id) {
            <button type="button" [class.active]="activeTab() === tab.id" (click)="selectTab(tab.id)">{{ t(tab.labelKey) }}</button>
          }
        </nav>

        @switch (activeTab()) {
          @case ('overview') {
            <section class="card grid">
              <article><span>{{ t('catalog.marketplace.sellers.code') }}</span><strong>{{ seller()!.sellerCode }}</strong></article>
              <article><span>{{ t('catalog.marketplace.sellers.name') }}</span><strong>{{ seller()!.displayName }}</strong></article>
              <article><span>{{ t('columns.status') }}</span><strong>{{ t(statusKey(seller()!.status)) }}</strong></article>
              <article><span>{{ t('catalog.marketplace.sellers.linkedParty') }}</span><strong>{{ seller()!.partyId || '—' }}</strong></article>
              <article><span>{{ t('catalog.marketplace.sellers.linkedCustomer') }}</span><strong>{{ seller()!.customerAccountId || '—' }}</strong></article>
              <article><span>{{ t('catalog.marketplace.sellers.kyc') }}</span><strong>{{ seller()!.kycReference || '—' }}</strong></article>
              <article><span>{{ t('catalog.marketplace.sellers.detail.returnPolicy') }}</span><strong>{{ seller()!.returnPolicySummary || '—' }}</strong></article>
              <article><span>{{ t('catalog.marketplace.sellers.detail.slaPolicy') }}</span><strong>{{ seller()!.slaPolicySummary || '—' }}</strong></article>
              <article><span>{{ t('catalog.marketplace.sellers.detail.lastReason') }}</span><strong>{{ seller()!.lastStatusReason || '—' }}</strong></article>
            </section>
            <section class="card form">
              <h3>{{ t('catalog.marketplace.sellers.detail.bindingTitle') }}</h3>
              <label>
                <span>{{ t('catalog.marketplace.sellers.linkedParty') }}</span>
                <ain-party-selector [(ngModel)]="bindingPartyId" name="bindingPartyId" />
              </label>
              <label>
                <span>{{ t('catalog.marketplace.sellers.linkedCustomer') }}</span>
                <ain-customer-selector [(ngModel)]="bindingCustomerId" name="bindingCustomerId" />
              </label>
              <label>
                <span>{{ t('catalog.marketplace.sellers.kyc') }}</span>
                <input pInputText [(ngModel)]="bindingKycReference" name="bindingKycReference" class="full" />
              </label>
              <button pButton type="button" [label]="t('common.save')" [disabled]="acting()" (click)="saveBindings()"></button>
            </section>
          }
          @case ('documents') {
            <ain-info-hint
              [titleKey]="'catalog.marketplace.supplier.documents.title'"
              [descriptionKey]="'catalog.marketplace.supplier.documents.description'" />
            @if (documentsLoading()) {
              <ain-state type="loading" [title]="t('catalog.loading')" />
            } @else if (!documents().length) {
              <ain-state type="empty" [title]="t('catalog.storefront.empty')" />
            } @else {
              <p-table [value]="documents()" [paginator]="true" [rows]="10">
                <ng-template pTemplate="header">
                  <tr>
                    <th>{{ t('catalog.marketplace.supplier.documents.title') }}</th>
                    <th>{{ t('columns.status') }}</th>
                    <th>{{ t('catalog.marketplace.supplier.documents.reviewNotes') }}</th>
                    <th></th>
                  </tr>
                </ng-template>
                <ng-template pTemplate="body" let-row>
                  <tr>
                    <td>{{ t(documentTypeKey(row.documentType)) }} (v{{ row.version }})</td>
                    <td>{{ t(documentStatusKey(row.status)) }}</td>
                    <td>{{ row.reviewNotes || '—' }}</td>
                    <td class="actions">
                      <button pButton type="button" size="small" severity="secondary" [label]="t('shared.attachments.view')" [disabled]="acting()" (click)="viewDocument(row)"></button>
                      <button pButton type="button" size="small" severity="secondary" [label]="t('shared.attachments.download')" [disabled]="acting()" (click)="downloadDocument(row)"></button>
                      @if (row.status !== 'Verified') {
                        <button pButton type="button" size="small" [label]="t('catalog.marketplace.sellers.approve')" [disabled]="acting()" (click)="verifyDocument(row)"></button>
                      }
                      @if (row.status !== 'Rejected') {
                        <button pButton type="button" size="small" severity="danger" [label]="t('catalog.marketplace.sellers.reject')" [disabled]="acting()" (click)="openRejectDocument(row)"></button>
                      }
                    </td>
                  </tr>
                </ng-template>
              </p-table>
            }
          }
          @case ('offers') {
            <div class="toolbar">
              <button pButton type="button" [label]="t('catalog.marketplace.sellers.detail.createOffer')" [disabled]="!canManageOffers()" (click)="openCreateOffer()"></button>
            </div>
            @if (offersLoading()) {
              <ain-state type="loading" [title]="t('catalog.loading')" />
            } @else if (!offers().length) {
              <ain-state type="empty" [title]="t('catalog.storefront.empty')" />
            } @else {
              <p-table [value]="offers()" [paginator]="true" [rows]="10">
                <ng-template pTemplate="header">
                  <tr>
                    <th>{{ t('catalog.marketplace.sellers.detail.offers') }}</th>
                    <th>{{ t('catalog.marketplace.dashboard.currency') }}</th>
                    <th>{{ t('catalog.marketplace.ops.amount') }}</th>
                    <th>{{ t('columns.status') }}</th>
                    <th></th>
                  </tr>
                </ng-template>
                <ng-template pTemplate="body" let-row>
                  <tr>
                    <td>{{ offeringLabel(row.offeringId) }}</td>
                    <td>{{ row.currencyCode }}</td>
                    <td>{{ row.price | ainAmount:2 }}</td>
                    <td>{{ row.isActive ? t('catalog.marketplace.sellers.status.approved') : t('catalog.marketplace.storefront.status.archived') }}</td>
                    <td class="actions">
                      @if (row.isActive) {
                        <button pButton type="button" size="small" severity="secondary" [label]="t('catalog.marketplace.sellers.detail.deactivateOffer')" [disabled]="acting()" (click)="deactivateOffer(row.id)"></button>
                      } @else if (canManageOffers()) {
                        <button pButton type="button" size="small" [label]="t('catalog.marketplace.sellers.detail.activateOffer')" [disabled]="acting()" (click)="activateOffer(row.id)"></button>
                      }
                    </td>
                  </tr>
                </ng-template>
              </p-table>
            }
          }
          @case ('orders') {
            @if (ordersLoading()) {
              <ain-state type="loading" [title]="t('catalog.loading')" />
            } @else if (!orders().length) {
              <ain-state type="empty" [title]="t('catalog.storefront.empty')" />
            } @else {
              <p-table [value]="orders()" [paginator]="true" [rows]="10">
                <ng-template pTemplate="header">
                  <tr>
                    <th>{{ t('catalog.marketplace.dashboard.order') }}</th>
                    <th>{{ t('columns.status') }}</th>
                    <th>{{ t('catalog.marketplace.ops.amount') }}</th>
                    <th>{{ t('catalog.marketplace.dashboard.created') }}</th>
                  </tr>
                </ng-template>
                <ng-template pTemplate="body" let-row>
                  <tr>
                    <td>{{ row.id }}</td>
                    <td>{{ row.status }}</td>
                    <td>{{ row.grossAmount | ainAmount:2 }} {{ row.currencyCode }}</td>
                    <td>{{ row.createdAtUtc | date:'medium' }}</td>
                  </tr>
                </ng-template>
              </p-table>
            }
          }
          @case ('settlements') {
            @if (settlementsLoading()) {
              <ain-state type="loading" [title]="t('catalog.loading')" />
            } @else if (!settlements().length) {
              <ain-state type="empty" [title]="t('catalog.storefront.empty')" />
            } @else {
              <p-table [value]="settlements()" [paginator]="true" [rows]="10">
                <ng-template pTemplate="header">
                  <tr>
                    <th>{{ t('catalog.marketplace.dashboard.order') }}</th>
                    <th>{{ t('catalog.marketplace.dashboard.netPayout') }}</th>
                    <th>{{ t('catalog.marketplace.dashboard.currency') }}</th>
                  </tr>
                </ng-template>
                <ng-template pTemplate="body" let-row>
                  <tr>
                    <td>{{ row.orderReferenceId }}</td>
                    <td>{{ row.netPayoutAmount | ainAmount:2 }}</td>
                    <td>{{ row.currencyCode }}</td>
                  </tr>
                </ng-template>
              </p-table>
            }
          }
          @case ('disputes') {
            @if (disputesLoading()) {
              <ain-state type="loading" [title]="t('catalog.loading')" />
            } @else if (!disputes().length) {
              <ain-state type="empty" [title]="t('catalog.storefront.empty')" />
            } @else {
              <p-table [value]="disputes()" [paginator]="true" [rows]="10">
                <ng-template pTemplate="header">
                  <tr>
                    <th>{{ t('catalog.marketplace.dashboard.order') }}</th>
                    <th>{{ t('columns.status') }}</th>
                    <th>{{ t('catalog.marketplace.dashboard.amount') }}</th>
                    <th>{{ t('catalog.marketplace.dashboard.reason') }}</th>
                  </tr>
                </ng-template>
                <ng-template pTemplate="body" let-row>
                  <tr>
                    <td>{{ row.orderReferenceId }}</td>
                    <td>{{ t(disputeStatusKey(row.status)) }}</td>
                    <td>{{ row.disputedAmount | ainAmount:2 }}</td>
                    <td>{{ row.reason }}</td>
                  </tr>
                </ng-template>
              </p-table>
            }
          }
        }

        @if (info()) { <p class="info">{{ info() }}</p> }
      }

      <p-dialog [header]="t('catalog.marketplace.sellers.detail.approveTitle')" [(visible)]="approveVisible" [modal]="true" [style]="{ width: '440px' }">
        <div class="form">
          <label>{{ t('catalog.marketplace.sellers.detail.approveReason') }}</label>
          <textarea pTextarea [(ngModel)]="approveReason" rows="3" class="full"></textarea>
        </div>
        <ng-template pTemplate="footer">
          <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="approveVisible = false"></button>
          <button pButton type="button" [label]="t('catalog.marketplace.sellers.approve')" [disabled]="acting()" (click)="confirmApprove()"></button>
        </ng-template>
      </p-dialog>

      <p-dialog [header]="statusDialogTitle()" [(visible)]="statusDialogVisible" [modal]="true" [style]="{ width: '440px' }">
        <div class="form">
          <label>{{ t('catalog.marketplace.sellers.detail.statusReason') }}</label>
          <textarea pTextarea [(ngModel)]="statusReason" rows="3" class="full"></textarea>
        </div>
        <ng-template pTemplate="footer">
          <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="statusDialogVisible = false"></button>
          <button pButton type="button" [label]="t('common.confirm')" [disabled]="acting()" (click)="confirmStatusAction()"></button>
        </ng-template>
      </p-dialog>

      <p-dialog [(visible)]="createOfferVisible" [modal]="true" [style]="{ width: '480px' }">
        <ng-template pTemplate="header">
          <div class="dialog-title">
            <span>{{ t('catalog.marketplace.sellers.detail.createOffer') }}</span>
            <ain-info-hint
              [titleKey]="'catalog.marketplace.help.createOffer.title'"
              [descriptionKey]="'catalog.marketplace.help.createOffer.description'"
              [bulletKeys]="[
                'catalog.marketplace.help.createOffer.bullets.offering',
                'catalog.marketplace.help.createOffer.bullets.price',
                'catalog.marketplace.help.createOffer.bullets.currency',
                'catalog.marketplace.help.createOffer.bullets.sla'
              ]" />
          </div>
        </ng-template>
        <div class="form">
          <label>
            <span>{{ t('catalog.marketplace.sellers.detail.offers') }}</span>
            <ain-offering-selector [(ngModel)]="newOfferOfferingId" name="newOfferOfferingId" />
          </label>
          <label>
            <span>{{ t('catalog.marketplace.ops.amount') }}</span>
            <p-inputNumber
              [(ngModel)]="newOfferPrice"
              name="newOfferPrice"
              [min]="0"
              [maxFractionDigits]="2"
              [useGrouping]="true"
              class="full" />
          </label>
          <label>
            <span>{{ t('catalog.marketplace.dashboard.currency') }}</span>
            <ain-currency-selector [(ngModel)]="newOfferCurrency" name="newOfferCurrency" [showSearch]="true" />
          </label>
          <label>
            <span>{{ t('catalog.marketplace.sellers.detail.slaHours') }}</span>
            <p-inputNumber
              [(ngModel)]="newOfferSlaHours"
              name="newOfferSlaHours"
              [min]="1"
              [useGrouping]="false"
              class="full" />
          </label>
        </div>
        <ng-template pTemplate="footer">
          <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="createOfferVisible = false"></button>
          <button pButton type="button" [label]="t('catalog.action.create')" [disabled]="acting()" (click)="submitCreateOffer()"></button>
        </ng-template>
      </p-dialog>

      <p-dialog
        [header]="documentPreviewTitle()"
        [visible]="documentPreviewOpen()"
        [modal]="true"
        [style]="{ width: 'min(920px, 96vw)' }"
        (onHide)="closeDocumentPreview()">
        @if (documentPreviewLoading()) {
          <ain-state type="loading" [title]="t('catalog.loading')" />
        } @else if (documentPreviewError()) {
          <ain-state type="error" [title]="documentPreviewError()!" />
        } @else if (documentPreviewMode() === 'pdf' && safeDocumentPreviewUrl()) {
          <iframe class="document-preview-frame" [src]="safeDocumentPreviewUrl()!" [title]="documentPreviewTitle()"></iframe>
        } @else if (documentPreviewMode() === 'image' && safeDocumentPreviewUrl()) {
          <img class="document-preview-image" [src]="safeDocumentPreviewUrl()!" [alt]="documentPreviewTitle()" />
        }
      </p-dialog>

      <p-dialog [header]="t('catalog.marketplace.supplier.documents.rejectReason')" [(visible)]="rejectDocumentVisible" [modal]="true" [style]="{ width: '440px' }">
        <div class="form">
          <textarea pTextarea [(ngModel)]="rejectDocumentReason" rows="3" class="full"></textarea>
        </div>
        <ng-template pTemplate="footer">
          <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="rejectDocumentVisible = false"></button>
          <button pButton type="button" severity="danger" [label]="t('catalog.marketplace.sellers.reject')" [disabled]="acting()" (click)="confirmRejectDocument()"></button>
        </ng-template>
      </p-dialog>
    </ain-page>
  `,
  styles: [`
    .toolbar { display: flex; gap: 8px; align-items: center; flex-wrap: wrap; margin-bottom: 8px; }
    .back-link { color: #1d4ed8; font-weight: 600; text-decoration: none; }
    .tabs { display: flex; gap: 8px; margin-bottom: 12px; flex-wrap: wrap; }
    .tabs button { border: 1px solid #e5e7eb; background: #fff; border-radius: 8px; padding: 6px 12px; cursor: pointer; }
    .tabs button.active { border-color: #1d4ed8; color: #1d4ed8; font-weight: 600; }
    .card { border: 1px solid #e5e7eb; border-radius: 12px; background: #fff; padding: 12px; }
    .grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(180px, 1fr)); gap: 12px; }
    .grid article { display: grid; gap: 4px; }
    .grid span { font-size: 0.8rem; color: #6b7280; }
    .dialog-title { display: flex; align-items: center; gap: 8px; }
    .form { display: grid; gap: 10px; }
    .form label { display: grid; gap: 6px; }
    .full { width: 100%; }
    :host ::ng-deep .full.p-inputnumber { width: 100%; }
    :host ::ng-deep .full.p-inputnumber .p-inputnumber-input { width: 100%; }
    .info { margin: 8px 0 0; color: #166534; font-weight: 600; }
    .actions { display: flex; gap: 6px; flex-wrap: wrap; justify-content: flex-end; }
    .document-preview-frame { width: 100%; min-height: 70vh; border: 0; border-radius: 8px; background: #f3f4f6; }
    .document-preview-image { display: block; max-width: 100%; max-height: 70vh; margin: 0 auto; border-radius: 8px; }
  `]
})
export class MarketplaceSellerDetailComponent implements OnInit, OnDestroy {
  private readonly service = inject(CatalogService);
  private readonly route = inject(ActivatedRoute);
  private readonly localization = inject(LocalizationService);
  private readonly contentService = inject(AttachmentContentService);
  private readonly sanitizer = inject(DomSanitizer);

  readonly loading = signal(true);
  readonly offersLoading = signal(false);
  readonly ordersLoading = signal(false);
  readonly settlementsLoading = signal(false);
  readonly disputesLoading = signal(false);
  readonly documentsLoading = signal(false);
  readonly acting = signal(false);
  readonly seller = signal<MarketplaceSeller | null>(null);
  readonly offers = signal<MarketplaceSellerOfferSummary[]>([]);
  readonly orders = signal<MarketplaceSellerOrder[]>([]);
  readonly settlements = signal<MarketplaceSettlementSummary[]>([]);
  readonly disputes = signal<MarketplaceDispute[]>([]);
  readonly documents = signal<MarketplaceSellerApplicationDocument[]>([]);
  readonly offerings = signal<CatalogOffering[]>([]);
  readonly info = signal('');
  readonly activeTab = signal<SellerTab>('overview');
  readonly listQueryParams = signal<{ q?: string }>({});

  readonly tabs: { id: SellerTab; labelKey: string }[] = [
    { id: 'overview', labelKey: 'catalog.marketplace.sellers.detail.overview' },
    { id: 'documents', labelKey: 'catalog.marketplace.sellers.detail.documents' },
    { id: 'offers', labelKey: 'catalog.marketplace.sellers.detail.offers' },
    { id: 'orders', labelKey: 'catalog.marketplace.sellers.detail.orders' },
    { id: 'settlements', labelKey: 'catalog.marketplace.sellers.detail.settlements' },
    { id: 'disputes', labelKey: 'catalog.marketplace.sellers.detail.disputes' }
  ];

  approveVisible = false;
  approveReason = '';
  statusDialogVisible = false;
  statusDialogMode: 'suspend' | 'reject' = 'suspend';
  statusReason = '';
  createOfferVisible = false;
  newOfferOfferingId: string | null = null;
  newOfferPrice = 100;
  newOfferCurrency: string | null = 'USD';
  newOfferSlaHours = 24;
  bindingPartyId: string | null = null;
  bindingCustomerId: string | null = null;
  bindingKycReference = '';
  rejectDocumentVisible = false;
  rejectDocumentReason = '';
  rejectDocumentTarget: MarketplaceSellerApplicationDocument | null = null;

  readonly documentPreviewOpen = signal(false);
  readonly documentPreviewLoading = signal(false);
  readonly documentPreviewError = signal('');
  readonly documentPreviewTitle = signal('');
  readonly documentPreviewMode = signal<'none' | 'pdf' | 'image'>('none');
  private readonly documentPreviewObjectUrl = signal<string | null>(null);
  readonly safeDocumentPreviewUrl = computed<SafeResourceUrl | null>(() => {
    const url = this.documentPreviewObjectUrl();
    return url ? this.sanitizer.bypassSecurityTrustResourceUrl(url) : null;
  });

  ngOnInit(): void {
    const q = this.route.snapshot.queryParamMap.get('q');
    this.listQueryParams.set(q ? { q } : {});
    this.service.listOfferings().subscribe({ next: rows => this.offerings.set(rows) });
    this.load();
  }

  pageTitle(): string {
    const seller = this.seller();
    return seller
      ? `${this.t('catalog.marketplace.sellers.detail.title')} — ${seller.displayName}`
      : this.t('catalog.marketplace.sellers.detail.title');
  }

  load(): void {
    const id = this.route.snapshot.paramMap.get('id') ?? '';
    if (!id) {
      this.seller.set(null);
      this.loading.set(false);
      return;
    }

    this.loading.set(true);
    this.info.set('');
    this.service.getMarketplaceSeller(id).subscribe({
      next: row => {
        this.seller.set(row);
        this.bindingPartyId = row.partyId ?? null;
        this.bindingCustomerId = row.customerAccountId ?? null;
        this.bindingKycReference = row.kycReference ?? '';
        this.loading.set(false);
        this.loadOffers(row.id);
        this.loadOrders(row.id);
        this.loadSettlements(row.id);
        this.loadDisputes(row.id);
        this.loadDocuments(row.id);
      },
      error: () => {
        this.seller.set(null);
        this.loading.set(false);
      }
    });
  }

  selectTab(tab: SellerTab): void {
    this.activeTab.set(tab);
  }

  loadOffers(sellerId: string): void {
    this.offersLoading.set(true);
    this.service.listMarketplaceOffers(undefined, sellerId).subscribe({
      next: rows => {
        this.offers.set(rows);
        this.offersLoading.set(false);
      },
      error: () => {
        this.offers.set([]);
        this.offersLoading.set(false);
      }
    });
  }

  loadOrders(sellerId: string): void {
    this.ordersLoading.set(true);
    this.service.listMarketplaceSellerOrders(sellerId).subscribe({
      next: response => {
        this.orders.set(response.items ?? []);
        this.ordersLoading.set(false);
      },
      error: () => {
        this.orders.set([]);
        this.ordersLoading.set(false);
      }
    });
  }

  loadSettlements(sellerId: string): void {
    this.settlementsLoading.set(true);
    this.service.listMarketplaceSellerSettlements(sellerId).subscribe({
      next: rows => {
        this.settlements.set(rows);
        this.settlementsLoading.set(false);
      },
      error: () => {
        this.settlements.set([]);
        this.settlementsLoading.set(false);
      }
    });
  }

  loadDisputes(sellerId: string): void {
    this.disputesLoading.set(true);
    this.service.listMarketplaceSellerDisputes(sellerId).subscribe({
      next: rows => {
        this.disputes.set(rows);
        this.disputesLoading.set(false);
      },
      error: () => {
        this.disputes.set([]);
        this.disputesLoading.set(false);
      }
    });
  }

  ngOnDestroy(): void {
    this.revokeDocumentPreviewUrl();
  }

  viewDocument(row: MarketplaceSellerApplicationDocument): void {
    const seller = this.seller();
    if (!seller) {
      return;
    }

    const fileName = this.documentFileBaseName(row);
    this.documentPreviewTitle.set(`${this.t(this.documentTypeKey(row.documentType))} (v${row.version})`);
    this.documentPreviewError.set('');
    this.revokeDocumentPreviewUrl();
    this.documentPreviewMode.set('none');
    this.documentPreviewOpen.set(true);
    this.documentPreviewLoading.set(true);

    const downloadUrl = this.service.marketplaceSellerDocumentContentUrl(seller.id, row.id);
    this.contentService.fetchBlob(downloadUrl).pipe(
      switchMap(blob => {
        const contentType = this.contentService.resolveContentType({ fileName, contentType: blob.type }, blob);
        if (!this.contentService.canPreviewInline(contentType, fileName)) {
          this.contentService.downloadBlob(blob, this.resolveDocumentFileName(fileName, blob));
          this.closeDocumentPreview();
          return of(null);
        }

        const previewBlob = this.contentService.toPreviewBlob(blob, contentType, fileName);
        const objectUrl = URL.createObjectURL(previewBlob);
        this.documentPreviewObjectUrl.set(objectUrl);
        this.documentPreviewMode.set(this.contentService.isPdf(contentType, fileName) ? 'pdf' : 'image');
        return of(null);
      }),
      catchError(() => {
        this.documentPreviewError.set(this.t('shared.attachments.previewFailed'));
        return of(null);
      })
    ).subscribe({ complete: () => this.documentPreviewLoading.set(false) });
  }

  downloadDocument(row: MarketplaceSellerApplicationDocument): void {
    const seller = this.seller();
    if (!seller) {
      return;
    }

    const fileName = this.documentFileBaseName(row);
    const downloadUrl = this.service.marketplaceSellerDocumentContentUrl(seller.id, row.id);
    this.contentService.fetchBlob(downloadUrl).subscribe({
      next: blob => this.contentService.downloadBlob(blob, this.resolveDocumentFileName(fileName, blob)),
      error: () => this.info.set(this.t('shared.attachments.downloadFailed'))
    });
  }

  closeDocumentPreview(): void {
    this.documentPreviewOpen.set(false);
    this.documentPreviewLoading.set(false);
    this.documentPreviewError.set('');
    this.documentPreviewMode.set('none');
    this.revokeDocumentPreviewUrl();
  }

  private documentFileBaseName(row: MarketplaceSellerApplicationDocument): string {
    return `${row.documentType}-v${row.version}`;
  }

  private resolveDocumentFileName(baseName: string, blob: Blob): string {
    const type = this.contentService.normalizeContentType(blob.type);
    if (type === 'application/pdf' || baseName.toLowerCase().endsWith('.pdf')) {
      return baseName.endsWith('.pdf') ? baseName : `${baseName}.pdf`;
    }
    if (type === 'image/png') {
      return baseName.endsWith('.png') ? baseName : `${baseName}.png`;
    }
    if (type === 'image/jpeg' || type === 'image/jpg') {
      return /\.jpe?g$/i.test(baseName) ? baseName : `${baseName}.jpg`;
    }
    if (type === 'image/webp') {
      return baseName.endsWith('.webp') ? baseName : `${baseName}.webp`;
    }
    return baseName;
  }

  private revokeDocumentPreviewUrl(): void {
    const url = this.documentPreviewObjectUrl();
    if (url) {
      URL.revokeObjectURL(url);
    }
    this.documentPreviewObjectUrl.set(null);
  }

  loadDocuments(sellerId: string): void {
    this.documentsLoading.set(true);
    this.service.listMarketplaceSellerDocuments(sellerId).subscribe({
      next: rows => {
        this.documents.set(rows);
        this.documentsLoading.set(false);
      },
      error: () => {
        this.documents.set([]);
        this.documentsLoading.set(false);
      }
    });
  }

  documentTypeKey(type: string): string {
    const map: Record<string, string> = {
      IdentityDocument: 'catalog.marketplace.supplier.documents.type.identityDocument',
      BusinessRegistration: 'catalog.marketplace.supplier.documents.type.businessRegistration',
      TaxCertificate: 'catalog.marketplace.supplier.documents.type.taxCertificate',
      BankAccountProof: 'catalog.marketplace.supplier.documents.type.bankAccountProof',
      Other: 'catalog.marketplace.supplier.documents.type.other'
    };
    return map[type] ?? 'catalog.marketplace.supplier.documents.type.other';
  }

  documentStatusKey(status: string): string {
    const map: Record<string, string> = {
      Uploaded: 'catalog.marketplace.supplier.documents.status.uploaded',
      UnderReview: 'catalog.marketplace.supplier.documents.status.underReview',
      Verified: 'catalog.marketplace.supplier.documents.status.verified',
      Rejected: 'catalog.marketplace.supplier.documents.status.rejected'
    };
    return map[status] ?? 'columns.status';
  }

  verifyDocument(row: MarketplaceSellerApplicationDocument): void {
    const seller = this.seller();
    if (!seller) return;
    this.acting.set(true);
    this.service.verifyMarketplaceSellerDocument(seller.id, row.id, {}).subscribe({
      next: () => {
        this.info.set(this.t('catalog.marketplace.supplier.documents.verified'));
        this.acting.set(false);
        this.loadDocuments(seller.id);
        this.service.getMarketplaceSeller(seller.id).subscribe({ next: updated => this.seller.set(updated) });
      },
      error: () => this.acting.set(false)
    });
  }

  openRejectDocument(row: MarketplaceSellerApplicationDocument): void {
    this.rejectDocumentTarget = row;
    this.rejectDocumentReason = '';
    this.rejectDocumentVisible = true;
  }

  confirmRejectDocument(): void {
    const seller = this.seller();
    const doc = this.rejectDocumentTarget;
    if (!seller || !doc || !this.rejectDocumentReason.trim()) {
      this.info.set(this.t('catalog.marketplace.supplier.documents.rejectReasonRequired'));
      return;
    }
    this.acting.set(true);
    this.service.rejectMarketplaceSellerDocument(seller.id, doc.id, { reason: this.rejectDocumentReason.trim() }).subscribe({
      next: () => {
        this.rejectDocumentVisible = false;
        this.info.set(this.t('catalog.marketplace.supplier.documents.rejected'));
        this.acting.set(false);
        this.loadDocuments(seller.id);
      },
      error: () => this.acting.set(false)
    });
  }

  canSubmit(): boolean {
    const status = this.seller()?.status;
    return status === 'Draft' || status === 'PendingKyc';
  }

  canApprove(): boolean {
    const status = this.seller()?.status;
    return status === 'Draft' || status === 'PendingKyc' || status === 'Submitted' || status === 'UnderReview';
  }

  canSuspend(): boolean {
    return this.seller()?.status === 'Approved';
  }

  canReactivate(): boolean {
    return this.seller()?.status === 'Suspended';
  }

  canReject(): boolean {
    const status = this.seller()?.status;
    return status === 'Draft' || status === 'PendingKyc' || status === 'Submitted' || status === 'UnderReview';
  }

  canManageOffers(): boolean {
    return this.seller()?.status === 'Approved';
  }

  saveBindings(): void {
    const seller = this.seller();
    if (!seller) return;
    this.acting.set(true);
    this.service.updateMarketplaceSeller(seller.id, {
      displayName: seller.displayName,
      partyId: this.bindingPartyId,
      customerAccountId: this.bindingCustomerId,
      kycReference: this.bindingKycReference.trim() || null,
      publicProfileSummary: seller.publicProfileSummary ?? null,
      returnPolicySummary: seller.returnPolicySummary ?? null,
      slaPolicySummary: seller.slaPolicySummary ?? null,
      complianceEvidenceReference: seller.complianceEvidenceReference ?? null,
      payoutProfileReference: seller.payoutProfileReference ?? null
    }).subscribe({
      next: updated => {
        this.seller.set(updated);
        this.info.set(this.t('catalog.marketplace.sellers.detail.bindingSaved'));
        this.acting.set(false);
      },
      error: () => this.acting.set(false)
    });
  }

  submitForReview(): void {
    const seller = this.seller();
    if (!seller) return;
    this.acting.set(true);
    this.service.submitMarketplaceSeller(seller.id, { actorReference: 'admin', reason: 'Submitted from admin' }).subscribe({
      next: updated => {
        this.seller.set(updated);
        this.info.set(this.t('catalog.marketplace.sellers.submitted'));
        this.acting.set(false);
      },
      error: () => this.acting.set(false)
    });
  }

  openApprove(): void {
    this.approveReason = '';
    this.approveVisible = true;
  }

  confirmApprove(): void {
    const seller = this.seller();
    if (!seller) return;
    if (!this.approveReason.trim()) {
      this.info.set(this.t('catalog.marketplace.sellers.detail.approveReasonRequired'));
      return;
    }
    this.acting.set(true);
    this.service.approveMarketplaceSeller(seller.id, { actorReference: 'admin', reason: this.approveReason.trim() }).subscribe({
      next: updated => {
        this.seller.set(updated);
        this.approveVisible = false;
        this.info.set(this.t('catalog.marketplace.sellers.approved'));
        this.acting.set(false);
      },
      error: () => this.acting.set(false)
    });
  }

  openStatusDialog(mode: 'suspend' | 'reject'): void {
    this.statusDialogMode = mode;
    this.statusReason = '';
    this.statusDialogVisible = true;
  }

  statusDialogTitle(): string {
    return this.statusDialogMode === 'suspend'
      ? this.t('catalog.marketplace.sellers.suspend')
      : this.t('catalog.marketplace.sellers.reject');
  }

  confirmStatusAction(): void {
    const seller = this.seller();
    if (!seller || !this.statusReason.trim()) {
      this.info.set(this.t('catalog.marketplace.sellers.detail.statusReasonRequired'));
      return;
    }
    this.acting.set(true);
    const payload = { actorReference: 'admin', reason: this.statusReason.trim() };
    const request = this.statusDialogMode === 'suspend'
      ? this.service.suspendMarketplaceSeller(seller.id, payload)
      : this.service.rejectMarketplaceSeller(seller.id, payload);
    request.subscribe({
      next: updated => {
        this.seller.set(updated);
        this.statusDialogVisible = false;
        this.info.set(this.t(this.statusDialogMode === 'suspend'
          ? 'catalog.marketplace.sellers.suspended'
          : 'catalog.marketplace.sellers.rejected'));
        this.loadOffers(seller.id);
        this.acting.set(false);
      },
      error: () => this.acting.set(false)
    });
  }

  reactivate(): void {
    const seller = this.seller();
    if (!seller) return;
    this.acting.set(true);
    this.service.reactivateMarketplaceSeller(seller.id, { actorReference: 'admin', reason: 'Reactivated from admin' }).subscribe({
      next: updated => {
        this.seller.set(updated);
        this.info.set(this.t('catalog.marketplace.sellers.reactivated'));
        this.acting.set(false);
      },
      error: () => this.acting.set(false)
    });
  }

  openCreateOffer(): void {
    this.newOfferOfferingId = null;
    this.newOfferPrice = 100;
    this.newOfferCurrency = 'USD';
    this.newOfferSlaHours = 24;
    this.createOfferVisible = true;
  }

  submitCreateOffer(): void {
    const seller = this.seller();
    const currencyCode = this.newOfferCurrency?.trim();
    if (!seller || !this.newOfferOfferingId || !currencyCode || this.newOfferPrice <= 0) {
      return;
    }

    this.acting.set(true);
    this.service.createMarketplaceOffer({
      sellerId: seller.id,
      offeringId: this.newOfferOfferingId,
      price: this.newOfferPrice,
      currencyCode,
      fulfillmentSlaHours: this.newOfferSlaHours
    }).subscribe({
      next: () => {
        this.createOfferVisible = false;
        this.info.set(this.t('catalog.marketplace.sellers.detail.offerCreated'));
        this.loadOffers(seller.id);
        this.acting.set(false);
      },
      error: () => this.acting.set(false)
    });
  }

  activateOffer(offerId: string): void {
    this.acting.set(true);
    this.service.activateMarketplaceOffer(offerId).subscribe({
      next: () => {
        const seller = this.seller();
        if (seller) this.loadOffers(seller.id);
        this.acting.set(false);
      },
      error: () => this.acting.set(false)
    });
  }

  deactivateOffer(offerId: string): void {
    this.acting.set(true);
    this.service.deactivateMarketplaceOffer(offerId).subscribe({
      next: () => {
        const seller = this.seller();
        if (seller) this.loadOffers(seller.id);
        this.acting.set(false);
      },
      error: () => this.acting.set(false)
    });
  }

  offeringLabel(offeringId: string): string {
    const offering = this.offerings().find(item => item.id === offeringId);
    return offering ? `${offering.code} - ${offering.title}` : offeringId;
  }

  statusKey(status: string): string {
    const normalized = status.charAt(0).toLowerCase() + status.slice(1);
    return `catalog.marketplace.sellers.status.${normalized}`;
  }

  disputeStatusKey(status: string): string {
    const normalized = status.charAt(0).toLowerCase() + status.slice(1);
    return `catalog.marketplace.disputes.status.${normalized}`;
  }

  t(key: string): string {
    return this.localization.translate(key);
  }
}
